// ScanWizardPublicPage — /scan/wizard?token=<scan_session_token>.
//
// Public wizard host (no LIFF). Drives the LIFF-free Phase-6 flow:
//
//   1. Read token from URL, GET /v1/patrols/scan/state to learn current
//      session state (officer_id resolved? assignments? finalize done?).
//   2. If officer_id is null AND tasks exist → render identity verify
//      card with two options: WebAuthn passkey OR face liveness.
//   3. After verify resolves identity, POST /v1/patrols/scan/finalize
//      to open submissions.
//   4. With submissions in hand, the post-Step-1 wizard (Step 2 / Step 3)
//      runs against scan_session-aware endpoints. (Step 2/3 dual-auth is
//      shipped in a follow-up PR — for now we display the submission set
//      + a recap so the operator can confirm the flow worked.)
//
// Why no LIFF: the three Step 1 verifications (device / face / geo) have
// no LINE-app dependency. WebAuthn + face match identify the officer
// directly; LIFF was redundant. Spec §8.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, AlertTriangle, CheckCircle2, Fingerprint, ScanFace,
  MapPin, Route as RouteIcon, ChevronRight,
} from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { apiFetch } from "../../lib/api-client.js";
import FaceVerifyOverlay, { type FaceAction } from "./FaceVerifyOverlay.js";
import { formatTaipeiRelative } from "../../lib/time.js";
import WizardRunner from "../liff/wizard/WizardRunner.js";
import StepTextResponse from "../liff/wizard/StepTextResponse.js";
import StepCameraEvidence from "../liff/wizard/StepCameraEvidence.js";
import StepFileEvidence from "../liff/wizard/StepFileEvidence.js";
import StepCanvasSignature from "../liff/wizard/StepCanvasSignature.js";
import { scanWizardCtx } from "../liff/wizard/types.js";

type AuthOptionsJSON = Parameters<typeof startAuthentication>[0]["optionsJSON"];

interface AvailableRoute {
  /** null = ad-hoc bucket (assignments not tied to a patrol route). */
  route_id: string | null;
  name: string;
  assignment_count: number;
}

interface NextOccurrence {
  /** Unix ms of the next scheduled task window-open (rrule occurrence
   *  minus grace_before_min). Frontend renders in operator's local
   *  timezone via Intl. */
  starts_at: number;
  /** "白天巡邏" / "夜間巡邏" / "無指定路線" / null if the route row
   *  was deleted between assignment creation and now. */
  route_name: string | null;
}

interface SessionState {
  patrol_event_id: string;
  tenant_id: string;
  location_id: string;
  location_label: string | null;
  location_code: string | null;
  location_building_name: string | null;
  location_floor_label: string | null;
  available_routes: AvailableRoute[];
  selected_route_id: string | null;
  /** True once the operator has chosen a route from available_routes.
   *  Gates the verify card — verify isn't shown until route is locked
   *  in. `selected_route_id === null && route_selected === true` is a
   *  legitimate state (= operator picked the ad-hoc bucket). */
  route_selected: boolean;
  /** Union of `verify_face` / `verify_device` across all tasks at this
   *  location × selected route. Drives which identity-verify cards
   *  the wizard renders — face only / passkey only / both / fall-back
   *  any-one. Null until /select-route has run. See backend
   *  scan-session.ts for full semantics. */
  required_verifications: { face: boolean; passkey: boolean; geo: boolean } | null;
  /** Soonest upcoming task at this location after now. Populated
   *  when available_routes is empty so we can show "下一個任務時段：
   *  X" instead of leaving the operator wondering when to come back. */
  next_occurrence: NextOccurrence | null;
  uid: string;
  ctr: number;
  officer_id: string | null;
  status: "init" | "identified" | "finalized" | "completed" | "expired";
  submission_ids: string[];
  exp: number;
  /** Per-method completion timestamps. Mirrors the backend's
   *  `verify_results` shape but typed minimally for the cards' done
   *  indicators. */
  verify_results?: {
    device?: { assertion_at: number };
    face?: { liveness_at: number; match_score?: number };
    geo?: { at: number; distance_m?: number };
  };
  /** Identity factors the resolved officer hasn't enrolled for — face
   *  has no descriptors on file / passkey has no active credentials.
   *  Computed at /select-route in the LIFF flow (when officer_id is
   *  known via x-actor-user-id) or null in the public scan flow.
   *  Drives the「您尚未註冊…」call-to-action that links to /enroll-*. */
  enrollment_gaps?: { face: boolean; passkey: boolean } | null;
}

interface FinalizeResponse {
  ok: boolean;
  submissions: { id: string; assignment_id?: string; task_id?: string }[];
  strict_order_violation: { expected_location_id: string | null } | null;
  already_finalized?: boolean;
  error?: string;
}

/**
 * Map a WebAuthn / browser-native error into a zh-TW message. The raw
 * DOMException messages from Safari / Chrome are English ("The request
 * is not allowed by the user agent or the platform in the current
 * context, possibly because the user denied permission.") which is
 * jarring for operators. Specifically:
 *
 *   - NotAllowedError → user cancelled / denied biometric prompt
 *   - AbortError → operation aborted (timed out / cancelled by us)
 *   - InvalidStateError → credential already registered on this device
 *   - SecurityError → origin/RPID mismatch
 *   - TimeoutError → didn't complete in the allotted window
 *
 * Falls through to a generic 「裝置驗證失敗」 plus the (potentially
 * English) detail for anything else, so unknown errors are still
 * actionable in support tickets.
 */
function friendlyWebAuthnError(err: unknown): string {
  if (err && typeof err === "object" && "name" in err) {
    const name = (err as { name?: string }).name;
    switch (name) {
      case "NotAllowedError":
        return "已取消裝置驗證。請再次嘗試並完成生物辨識。";
      case "AbortError":
        return "裝置驗證已中斷，請再試一次。";
      case "InvalidStateError":
        return "此裝置已綁定其它人員，請改用其它驗證方式。";
      case "SecurityError":
        return "瀏覽器安全檢查未通過，請改用 Safari / Chrome 最新版重試。";
      case "TimeoutError":
        return "驗證已逾時，請再試一次。";
      case "NotSupportedError":
        return "此裝置不支援生物辨識，請改用人臉辨識。";
    }
  }
  return "裝置驗證失敗，請再試一次。";
}

/**
 * Translate backend `error` codes from the scan flow into operator-
 * friendly zh-TW copy. Returning the raw code (or worse, the raw
 * Response body JSON) is what got the operator the「{"error":
 * "scan_session_invalid_or_expired", ...}」blob on their screen.
 *
 * Categories:
 *   - scan_session_* — KV token gone or never identified
 *   - tag_unbound / replay_detected — NTAG tap problem (rare here,
 *     but the matcher can return them from scan/state during retries)
 *   - text_required / photo_required / etc — task requires evidence
 *     that hasn't been filled in yet
 *   - too_large / unsupported_type — upload-side rejections
 *   - wrong_status / forbidden_other_officer — staleness / wrong user
 *   - {kv,event_partition}_unavailable — server-side transient
 *
 * Unknown codes fall through to a generic 「操作失敗 (詳情代碼)」 so
 * support can still trace them in audit logs.
 */
function friendlyScanError(code: string, status: number): string {
  switch (code) {
    // Session lifecycle
    case "scan_session_required":
      return "尚未取得掃描身分，請重新感應地點標籤。";
    case "scan_session_invalid_or_expired":
      return "掃描身分已過期（5 分鐘上限），請重新感應地點標籤。";
    case "scan_session_not_identified":
      return "尚未完成身分驗證，請先完成裝置或人臉驗證。";
    case "submission_not_in_scan_session":
      return "此任務不屬於本次掃描，請重新感應。";

    // NTAG / tag
    case "tag_unbound":
      return "此標籤尚未綁定地點，請聯絡管理員。";
    case "replay_detected":
      return "偵測到重複感應，請稍候再試。";

    // Route picker
    case "route_not_in_available_set":
      return "所選路線目前不在可選範圍內。請重新感應標籤。";

    // Identity verify
    case "credential_not_found":
      return "找不到此裝置的綁定，請先到 LINE 完成裝置綁定。";
    case "face_not_enrolled":
      return "尚未註冊人臉，請先到 LINE 完成人臉註冊。";
    case "face_no_match":
      return "人臉比對未通過，請正視鏡頭重試或改用裝置驗證。";
    case "face_low_score":
      return "人臉影像品質不足，請於光線充足處重試。";

    // Submission evidence rules
    case "text_required":      return "本任務需要填寫文字回應後才能送出。";
    case "photo_required":     return "本任務需要拍照後才能送出。";
    case "file_required":      return "本任務需要上傳檔案後才能送出。";
    case "signature_required": return "本任務需要簽名後才能送出。";
    case "device_required":    return "本任務需要先完成裝置驗證。";
    case "face_required":      return "本任務需要先完成人臉驗證。";
    case "geo_required":       return "本任務需要先完成位置驗證。";

    // Skip
    case "skip_not_allowed":  return "本任務不允許跳過，請完成後再送出。";
    case "reason_required":   return "請填寫跳過原因後再送出。";

    // Upload
    case "too_large":         return "檔案過大，每個檔案上限 10 MB。";
    case "unsupported_type":  return "不支援的檔案格式。";
    case "missing_file_field":     return "上傳請求缺少檔案。";
    case "missing_submission_id":  return "上傳請求缺少任務識別。";
    case "bad_form_data":     return "上傳格式錯誤，請重試。";

    // Submission state
    case "not_found":
    case "submission_not_found":
      return "找不到此任務記錄，可能已過期。";
    case "forbidden_other_officer":
      return "此任務屬於其他巡邏員，無法操作。";
    case "wrong_status":
      return "此任務目前狀態不允許此操作。";
    case "submission_partition_lost":
      return "任務資料定位失敗，請聯絡管理員。";

    // Server-side transient
    case "kv_unavailable":
    case "event_partition_unavailable":
    case "r2_unavailable":
      return "後端服務暫時不可用，請稍候再試。";
  }
  if (status === 401) return "登入或感應身分已失效，請重新感應。";
  if (status === 403) return "您沒有此操作的權限。";
  if (status === 404) return "找不到此資源。";
  if (status === 409) return "此操作目前無法完成（狀態衝突）。";
  if (status === 413) return "資料過大，請縮小後重試。";
  if (status === 415) return "格式不支援。";
  if (status === 422) return "資料未通過驗證，請檢查後重試。";
  if (status === 503) return "服務暫時不可用，請稍候再試。";
  return `操作失敗${code ? `（${code}）` : ""}，請再試一次。`;
}

/**
 * Pull a backend `error` code out of an apiFetch Response and map it
 * through friendlyScanError. Defensive against non-JSON bodies (some
 * gateway edges return plain text on early-stage failures).
 */
async function readScanError(r: Response): Promise<string> {
  let code = "";
  try {
    const body = await r.clone().json() as { error?: string };
    if (typeof body?.error === "string") code = body.error;
  } catch { /* fall through to text */ }
  return friendlyScanError(code, r.status);
}


export default function ScanWizardPublicPage(): React.ReactElement {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"idle" | "route" | "device" | "face" | "geo" | "finalize">("idle");
  const [finalize, setFinalize] = useState<FinalizeResponse | null>(null);
  /** When set, the FaceVerifyOverlay is mounted. Reset to null when
   *  the overlay finishes (success / cancel / unrecoverable failure). */
  const [faceChallenge, setFaceChallenge] = useState<{ challengeId: string; action: FaceAction } | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const r = await apiFetch("/v1/patrols/scan/state", { headers: { "x-scan-session": token } });
      if (!r.ok) {
        setError(await readScanError(r));
        return;
      }
      setState((await r.json()) as SessionState);
    } catch (e) {
      setError((e as Error).message ?? "連線中斷，請檢查網路後再試。");
    }
  }, [token]);

  useEffect(() => { void reload(); }, [reload]);

  const callFinalize = useCallback(async () => {
    setBusy("finalize");
    try {
      const r = await apiFetch("/v1/patrols/scan/finalize", {
        method: "POST",
        headers: { "x-scan-session": token },
      });
      const body = (await r.json().catch(() => ({}))) as FinalizeResponse;
      if (!r.ok) {
        setError(friendlyScanError(body.error ?? "", r.status));
        return;
      }
      setFinalize(body);
      await reload();
    } finally {
      setBusy("idle");
    }
  }, [token, reload]);

  // Auto-finalize once identity is resolved AND no submissions yet.
  useEffect(() => {
    if (state?.status === "identified" && state.submission_ids.length === 0 && busy === "idle") {
      void callFinalize();
    }
  }, [state, callFinalize, busy]);

  const selectRoute = async (routeId: string | null): Promise<void> => {
    setError(null);
    setBusy("route");
    try {
      const r = await apiFetch("/v1/patrols/scan/select-route", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        // JSON.stringify keeps `route_id: null` distinct from undefined
        // (= ad-hoc picked vs nothing picked) so the backend's
        // "route_id_required" guard fires only when the field is
        // genuinely missing.
        body: JSON.stringify({ route_id: routeId }),
      });
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!r.ok || !body.ok) {
        setError(friendlyScanError(body.error ?? "", r.status));
        return;
      }
      await reload();
    } catch (e) {
      setError("連線中斷，請檢查網路後再試。");
    } finally {
      setBusy("idle");
    }
  };

  const verifyDevice = async (): Promise<void> => {
    setError(null);
    setBusy("device");
    try {
      const startResp = await apiFetch("/v1/patrols/scan/verify/device/start", {
        method: "POST",
        headers: { "x-scan-session": token },
      });
      const startBody = (await startResp.json().catch(() => ({}))) as { options?: AuthOptionsJSON; error?: string };
      if (!startResp.ok || !startBody.options) {
        setError(friendlyScanError(startBody.error ?? "", startResp.status));
        return;
      }
      const assertion = await startAuthentication({ optionsJSON: startBody.options });
      const finishResp = await apiFetch("/v1/patrols/scan/verify/device/finish", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        body: JSON.stringify({ assertionResponse: assertion }),
      });
      const finishBody = (await finishResp.json().catch(() => ({}))) as { ok?: boolean; officer_id?: string; error?: string };
      if (!finishResp.ok || !finishBody.ok) {
        setError(friendlyScanError(finishBody.error ?? "", finishResp.status));
        return;
      }
      await reload();
    } catch (e) {
      // startAuthentication throws DOMException on cancel / timeout —
      // map to zh-TW so operators don't see raw English browser text.
      setError(friendlyWebAuthnError(e));
    } finally {
      setBusy("idle");
    }
  };

  /**
   * Open the FaceVerifyOverlay. We only fetch the challenge here (so
   * the failure mode "challenge endpoint is broken" surfaces before we
   * hand off to the overlay), then set state to mount the overlay.
   * Camera / models / detection / submit all live inside the overlay —
   * keeps this page's component tree small and lets the overlay own its
   * camera lifecycle.
   */
  const verifyFace = async (): Promise<void> => {
    setError(null);
    setBusy("face");
    try {
      const cr = await apiFetch("/v1/patrols/scan/verify/face/challenge", { headers: { "x-scan-session": token } });
      const challenge = (await cr.json().catch(() => ({}))) as { challenge_id?: string; action?: FaceAction; error?: string };
      if (!cr.ok || !challenge.challenge_id || !challenge.action) {
        setError(friendlyScanError(challenge.error ?? "", cr.status));
        setBusy("idle");
        return;
      }
      setFaceChallenge({ challengeId: challenge.challenge_id, action: challenge.action });
      // busy stays "face" while the overlay is open so the verify card
      // disables both buttons. Overlay's onCancel / onSuccess resets it.
    } catch (e) {
      setError("無法開始人臉驗證，請稍候再試。");
      setBusy("idle");
    }
  };

  /**
   * Geo verification — pull the operator's current GPS coords via the
   * browser geolocation API and POST to /scan/verify/geo. The backend
   * (apps/patrol-api/src/verify.ts:767) does the haversine + accuracy
   * check against the location's geofence_radius_m and writes
   * verify_results.geo on success. We send `accuracy_m` from the
   * Geolocation API so the backend can require the GPS reading itself
   * to be precise enough — bouncing a 200m-accuracy reading through a
   * 30m geofence would let cheaters in.
   *
   * UX: the prompt shows a small spinner; on success the card flips
   * to「位置已驗證」, on permission denial / off-radius we surface a
   * friendly error so the operator can re-position or open settings.
   */
  const verifyGeo = async (): Promise<void> => {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("此裝置不支援位置驗證，請改用其他驗證方式或聯絡管理員。");
      return;
    }
    setBusy("geo");
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, maximumAge: 0, timeout: 15_000,
        });
      });
      const r = await apiFetch("/v1/patrols/scan/verify/geo", {
        method: "POST",
        headers: { "x-scan-session": token, "content-type": "application/json" },
        body: JSON.stringify({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        }),
      });
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!r.ok || !body.ok) {
        setError(friendlyScanError(body.error ?? "", r.status));
        return;
      }
      await reload();
    } catch (e) {
      const err = e as GeolocationPositionError | Error;
      // GeolocationPositionError uses code 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT.
      const code = (err as GeolocationPositionError).code;
      setError(
        code === 1 ? "需要位置權限才能完成驗證。請至瀏覽器設定開啟位置存取。"
        : code === 2 ? "目前無法取得 GPS 位置，請到戶外或靠近窗邊重試。"
        : code === 3 ? "GPS 取得逾時，請稍候再試。"
        : "位置驗證失敗，請稍候再試。",
      );
    } finally {
      setBusy((b) => (b === "geo" ? "idle" : b));
    }
  };

  // Face verify in progress → the overlay takes the whole screen until
  // it succeeds, the operator cancels, or the unrecoverable-failure
  // branch resolves. Rendered as an early return so the picker / verify
  // card underneath don't fight for input focus.
  if (faceChallenge) {
    return (
      <FaceVerifyOverlay
        token={token}
        challengeId={faceChallenge.challengeId}
        action={faceChallenge.action}
        locationBuilding={state?.location_building_name ?? null}
        locationFloor={state?.location_floor_label ?? null}
        locationLabel={state?.location_label ?? null}
        locationCode={state?.location_code ?? null}
        onSuccess={async () => {
          setFaceChallenge(null);
          setBusy("idle");
          await reload();
        }}
        onCancel={() => {
          setFaceChallenge(null);
          setBusy("idle");
        }}
      />
    );
  }

  if (!token) {
    return (
      <Frame>
        <AlertTriangle className="h-12 w-12 text-danger mb-4" />
        <p className="text-base font-medium mb-1">缺少 token 參數</p>
        <p className="text-xs text-text-muted">請從 NFC 感應後的頁面進入此精靈。</p>
      </Frame>
    );
  }

  if (!state && !error) {
    return (
      <Frame>
        <Loader2 className="h-12 w-12 animate-spin text-accent mb-4" />
        <p className="text-sm text-text-secondary">讀取掃描階段中…</p>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame>
        <AlertTriangle className="h-12 w-12 text-danger mb-4" />
        <p className="text-base font-medium mb-1">{error}</p>
        <button onClick={() => { setError(null); void reload(); }} className="btn-secondary mt-3">
          重試
        </button>
      </Frame>
    );
  }

  // Route not yet selected → route picker. The picker shows BEFORE
  // identity verify because some assignments don't require Passkey /
  // face (verify_* toggles are per-task), and because operators may
  // not yet be enrolled — the verify methods that show depend on the
  // task set under the picked route, which we don't know until they
  // pick.
  if (state && !state.route_selected) {
    const routes = state.available_routes;
    if (routes.length === 0) {
      const next = state.next_occurrence;
      return (
        <Frame>
          <LocationBanner
            building={state.location_building_name}
            floor={state.location_floor_label}
            label={state.location_label}
            code={state.location_code}
          />
          <AlertTriangle className="h-12 w-12 text-warning mb-3" />
          <p className="text-base font-medium mb-1">此地點目前無進行中的任務</p>
          {next ? (
            <NextOccurrenceCard
              startsAt={next.starts_at}
              routeName={next.route_name}
              onArrival={() => { void reload(); }}
            />
          ) : (
            <p className="text-xs text-text-muted text-center">
              目前未排定後續任務。請聯絡管理員確認班表。
            </p>
          )}
        </Frame>
      );
    }
    return (
      <Frame>
        <LocationBanner
          building={state.location_building_name}
          floor={state.location_floor_label}
          label={state.location_label}
          code={state.location_code}
        />
        <RouteIcon className="h-12 w-12 text-accent mb-3" />
        <p className="text-base font-medium mb-1">請選擇路線</p>
        <p className="text-xs text-text-muted text-center mb-4">
          目前在這個地點有以下任務分組，請選擇你要執行的路線。
        </p>
        <div className="space-y-2 w-full">
          {routes.map((r) => (
            <button
              key={r.route_id ?? "__adhoc__"}
              onClick={() => void selectRoute(r.route_id)}
              disabled={busy !== "idle"}
              className="w-full rounded-xl bg-bg-raised hover:bg-bg-hover disabled:opacity-50 text-text-primary text-sm font-medium px-4 py-3 inline-flex items-center justify-between gap-2"
            >
              <div className="flex-1 min-w-0 text-left">
                <p className="truncate">{r.name}</p>
                <p className="text-2xs text-text-muted mt-0.5">
                  {r.assignment_count} 個任務
                </p>
              </div>
              {busy === "route"
                ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />}
            </button>
          ))}
        </div>
      </Frame>
    );
  }

  // Identity not yet fully resolved → Step 0 verify card(s). Gated on
  // status === "init" rather than `officer_id == null`: when the task
  // requires BOTH passkey + face, the first verify resolves
  // officer_id but the backend keeps status at "init" until the SECOND
  // method also completes. Gating on officer_id would hide this card
  // after one verify, dropping the operator straight onto the
  // empty-submissions screen with no way to finish.
  if (state && state.status === "init" && state.route_selected) {
    const req = state.required_verifications;
    const requireFace = req?.face ?? false;
    const requirePasskey = req?.passkey ?? false;
    const requireGeo = req?.geo ?? false;
    const legacyAnyOk = !requireFace && !requirePasskey && !requireGeo;
    const passkeyDone = !!state.verify_results?.device?.assertion_at;
    const faceDone = !!state.verify_results?.face?.liveness_at;
    const geoDone = !!state.verify_results?.geo?.at;
    // Count requireds for the prompt copy. "兩項" / "三項" reads better
    // than the 'X 項' template that loops through factor names — Chinese
    // counter words get awkward fast.
    const requiredCount = (requireFace ? 1 : 0) + (requirePasskey ? 1 : 0) + (requireGeo ? 1 : 0);
    const promptCopy = legacyAnyOk
      ? "完成下列任一驗證即可繼續執行任務。"
      : requiredCount === 1
        ? "請完成下方驗證以繼續。"
        : `本次任務需完成 ${requiredCount === 2 ? "兩" : "三"} 項驗證，順序不限。`;
    const gaps = state.enrollment_gaps ?? null;
    const showFaceGap = !!(gaps?.face && requireFace && !faceDone);
    const showPasskeyGap = !!(gaps?.passkey && requirePasskey && !passkeyDone);
    return (
      <Frame>
        <LocationBanner
          building={state.location_building_name}
          floor={state.location_floor_label}
          label={state.location_label}
          code={state.location_code}
        />
        <ScanFace className="h-12 w-12 text-accent mb-3" />
        <p className="text-base font-medium mb-1">請驗證身分</p>
        <p className="text-xs text-text-muted text-center mb-4">{promptCopy}</p>
        <div className="space-y-2 w-full">
          {(requirePasskey || legacyAnyOk) && (
            <button
              onClick={() => void verifyDevice()}
              disabled={busy !== "idle" || passkeyDone}
              className={
                "w-full h-12 rounded-xl text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2 " +
                (passkeyDone
                  ? "bg-success/15 text-success border border-success/30"
                  : "bg-accent text-text-inverse disabled:bg-bg-raised disabled:text-text-muted")
              }
            >
              {passkeyDone
                ? <><CheckCircle2 className="h-4 w-4" />Passkey 已驗證</>
                : busy === "device"
                  ? <><Loader2 className="h-4 w-4 animate-spin" />驗證中…</>
                  : <><Fingerprint className="h-4 w-4" />使用此裝置 Passkey</>}
            </button>
          )}
          {(requireFace || legacyAnyOk) && (
            <button
              onClick={() => void verifyFace()}
              disabled={busy !== "idle" || faceDone}
              className={
                "w-full h-12 rounded-xl text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2 " +
                (faceDone
                  ? "bg-success/15 text-success border border-success/30"
                  : "bg-bg-raised text-text-primary disabled:text-text-muted")
              }
            >
              {faceDone
                ? <><CheckCircle2 className="h-4 w-4" />人臉已驗證</>
                : busy === "face"
                  ? <><Loader2 className="h-4 w-4 animate-spin" />驗證中…</>
                  : <><ScanFace className="h-4 w-4" />人臉辨識</>}
            </button>
          )}
          {requireGeo && (
            <button
              onClick={() => void verifyGeo()}
              disabled={busy !== "idle" || geoDone}
              className={
                "w-full h-12 rounded-xl text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2 " +
                (geoDone
                  ? "bg-success/15 text-success border border-success/30"
                  : "bg-bg-raised text-text-primary disabled:text-text-muted")
              }
            >
              {geoDone
                ? <><CheckCircle2 className="h-4 w-4" />位置已驗證</>
                : busy === "geo"
                  ? <><Loader2 className="h-4 w-4 animate-spin" />取得位置中…</>
                  : <><MapPin className="h-4 w-4" />驗證所在位置</>}
            </button>
          )}
        </div>
        {(showFaceGap || showPasskeyGap) && (
          <div className="mt-4 w-full rounded-xl bg-warning/10 border border-warning/30 p-3">
            <p className="text-xs text-warning font-medium mb-1">
              {showFaceGap && showPasskeyGap
                ? "您尚未註冊人臉與 Passkey"
                : showFaceGap
                  ? "您尚未註冊人臉"
                  : "您尚未註冊 Passkey"}
            </p>
            <p className="text-2xs text-text-muted leading-relaxed">
              本次任務要求{showFaceGap && showPasskeyGap ? "兩項" : "此項"}驗證，但您的帳號尚無註冊資料。請聯絡管理員產生註冊連結，完成註冊後再回此頁。
            </p>
          </div>
        )}
        {!showFaceGap && !showPasskeyGap && (
          <p className="text-2xs text-text-muted text-center mt-4">
            提示：必須先在 dashboard 由管理員產生「人員註冊連結」並完成 passkey 或人臉註冊。
          </p>
        )}
      </Frame>
    );
  }

  // Identity resolved, awaiting / running finalize.
  if (state && (state.status === "identified" || busy === "finalize")) {
    return (
      <Frame>
        <Loader2 className="h-12 w-12 animate-spin text-accent mb-4" />
        <p className="text-base font-medium mb-1">身分已驗證</p>
        <p className="text-xs text-text-muted text-center">建立任務精靈中…</p>
      </Frame>
    );
  }

  // Finalized — Step 1 done, Step 2/3 happen here inline (no LIFF
  // detour). WizardRunner is the shared orchestrator also used by
  // LIFF; the scan ctx targets /v1/patrols/scan/* with the
  // x-scan-session header.
  if (state && state.submission_ids.length === 0) {
    // Edge case: finalize returned without producing submissions.
    // Most commonly hit when strict_order_violation skipped task
    // creation. Show the original celebration so the operator knows
    // the tap succeeded.
    return (
      <Frame>
        <CheckCircle2 className="h-12 w-12 text-success mb-3" />
        <p className="text-base font-medium mb-1">身分驗證完成</p>
        <p className="text-sm text-text-secondary mb-4 text-center">本次掃描沒有產生任務</p>
        {finalize?.strict_order_violation && (
          <div className="rounded-xl bg-warning/10 border border-warning/40 p-3 mb-3 text-xs">
            <p className="inline-flex items-center gap-1 font-medium text-warning mb-1">
              <MapPin className="h-3.5 w-3.5" />路線順序提示
            </p>
            <p className="text-text-secondary">
              此 location 不是預期的下一站，目前路線設為強制順序，已停止建立任務。
            </p>
          </div>
        )}
      </Frame>
    );
  }

  const ctx = scanWizardCtx(token);
  return (
    <Frame>
      <LocationBanner
        building={state?.location_building_name ?? null}
        floor={state?.location_floor_label ?? null}
        label={state?.location_label ?? null}
        code={state?.location_code ?? null}
      />
      <WizardRunner
        ctx={ctx}
        submissionIds={state?.submission_ids ?? []}
        renderText={({ text, onTextChange }) => <StepTextResponse text={text} onTextChange={onTextChange} />}
        renderCamera={({ data: d, onComplete }) => <StepCameraEvidence ctx={ctx} data={d} onComplete={onComplete} />}
        renderFile={({ data: d, onComplete }) => <StepFileEvidence ctx={ctx} data={d} onComplete={onComplete} />}
        renderCanvas={({ data: d, onComplete }) => <StepCanvasSignature ctx={ctx} data={d} onComplete={onComplete} />}
        doneScreen={
          <div className="w-full flex flex-col items-center text-center py-6">
            <CheckCircle2 className="h-12 w-12 text-success mb-3" />
            <p className="text-base font-medium mb-1">本次掃描的任務全部處理完成</p>
            <p className="text-xs text-text-muted">您可以離開此頁；管理員會收到結果通知。</p>
          </div>
        }
      />
    </Frame>
  );
}

/**
 * Card shown when the tapped location has no active route right now
 * but a future occurrence is queued. When that occurrence is more than
 * 3 minutes out, render it as a Taipei-relative chip ("今天 23:45" /
 * "明天 02:55" / "MM-DD HH:mm"). When it's ≤ 3 minutes out, swap to a
 * live mm:ss countdown so the operator can stand at the location and
 * watch the window open instead of guessing. When the countdown
 * crosses zero we fire `onArrival` so the page can reload state and
 * pick up the now-active route without a manual refresh.
 */
const COUNTDOWN_THRESHOLD_MS = 3 * 60 * 1000;
function NextOccurrenceCard({
  startsAt, routeName, onArrival,
}: {
  startsAt: number;
  routeName: string | null;
  onArrival: () => void;
}): React.ReactElement {
  const [now, setNow] = useState(() => Date.now());

  // Tick every second whenever we're inside or near the countdown
  // window. Once we're well outside the window the second-level tick
  // is wasted, so back off to 30 s — still snappy enough to drop into
  // countdown mode as soon as the operator stays on the page.
  useEffect(() => {
    const intervalMs = startsAt - now > COUNTDOWN_THRESHOLD_MS + 30_000 ? 30_000 : 1000;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [startsAt, now]);

  const diffMs = startsAt - now;
  const expired = diffMs <= 0;

  // Once we cross zero, keep re-polling `reload` every second until the
  // parent moves past this card (server returns `available_routes`
  // non-empty → LocationBanner UI switches to the route picker and
  // this component unmounts). Single-shot wasn't enough: phone clock
  // vs Worker clock can drift a few seconds, so the first reload often
  // lands while the server still sees the assignment as "next" rather
  // than "active right now" and we got stuck on 已開始 · 重新整理中….
  const onArrivalRef = useRef(onArrival);
  onArrivalRef.current = onArrival;
  useEffect(() => {
    if (!expired) return;
    onArrivalRef.current();
    const id = setInterval(() => onArrivalRef.current(), 1000);
    return () => clearInterval(id);
  }, [expired]);

  const inCountdown = diffMs > 0 && diffMs <= COUNTDOWN_THRESHOLD_MS;
  const totalSec = Math.max(0, Math.ceil(diffMs / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;

  return (
    <div className={
      "w-full mt-3 rounded-xl border p-3 text-center transition-colors " +
      (inCountdown ? "border-warning/50 bg-warning/10" : "border-border bg-bg-raised")
    }>
      <p className="text-2xs text-text-muted mb-0.5">下一個任務時段</p>
      {inCountdown ? (
        <p className="text-3xl font-bold font-mono tabular-nums text-warning leading-tight">
          {mm}:{ss.toString().padStart(2, "0")}
        </p>
      ) : diffMs <= 0 ? (
        <p className="text-sm font-semibold text-success font-mono tabular-nums">
          已開始 · 重新整理中…
        </p>
      ) : (
        <p className="text-sm font-semibold text-text-primary font-mono tabular-nums">
          {formatTaipeiRelative(startsAt)}
        </p>
      )}
      {routeName && (
        <p className="text-xs text-text-secondary mt-1">
          路線：{routeName}
        </p>
      )}
    </div>
  );
}

/**
 * Inline chip at the top of every step that shows the operator which
 * location they just tapped. Both label and code may be null (tag
 * binding row missing them) — falls through to the bare code if only
 * one is present.
 */
/**
 * Compact pill chip showing the location hierarchy for the tapped
 * location: 建築物 › 樓層 › 地點 ( code ). Borderless / no-card
 * style per operator feedback (「不要有卡片，用膠囊或其他方式」). Wraps
 * onto multiple lines on narrow screens; segments with null values
 * are skipped without leaving stray separators.
 */
function LocationBanner({
  building, floor, label, code,
}: {
  building: string | null;
  floor: string | null;
  label: string | null;
  /** Retained for fall-back display when label is missing (legacy / mis-
   *  configured location) — normally the user wants 建築物 › 樓層 ›
   *  地點名稱 with no code suffix. */
  code: string | null;
}): React.ReactElement | null {
  // 膠囊呈現順序：建築物 › 樓層 › 地點名稱。code 不再附在後面 — 巡邏員
  // 要的是「我在哪一棟、第幾層、哪一個點」的人類可讀句子，不是內部代號。
  const locationText = label ?? code;
  const hasAny = building || floor || locationText;
  if (!hasAny) return null;
  const segments: { text: string; emphasised?: boolean }[] = [];
  if (building) segments.push({ text: building });
  if (floor) segments.push({ text: floor });
  if (locationText) segments.push({ text: locationText, emphasised: true });
  return (
    <div className="w-full flex justify-center mb-5">
      <div className="inline-flex items-center flex-wrap gap-x-1.5 gap-y-0.5 rounded-full bg-bg-raised/60 border border-border-subtle px-3 py-1.5 max-w-full">
        <MapPin className="h-3.5 w-3.5 text-accent shrink-0" />
        {segments.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-text-muted text-2xs">›</span>}
            <span className={s.emphasised
              ? "text-xs font-medium text-text-primary"
              : "text-2xs text-text-muted"}
            >
              {s.text}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Frame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="min-h-screen bg-bg-base text-text-base flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md flex flex-col items-center">
        <header className="flex items-center gap-2 mb-6 mt-2">
          <img
            src="/sentry-icon.svg"
            alt="SENTRY"
            className="h-8 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <span className="text-sm font-semibold tracking-wide">NEXUS</span>
        </header>
        {children}
      </div>
    </div>
  );
}
